import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, List, Progress, Typography, Button, Space, message, Row, Col, Tag, Tooltip, Modal } from 'antd';
import { categoryService, CategoryGamesResponse, Category } from '../services/categoryService';
import MainLayout from './layout/MainLayout';
import { AxiosError } from 'axios';
import { BookOutlined, RiseOutlined, FireOutlined, TrophyOutlined, AppstoreOutlined, CheckCircleOutlined, ClockCircleOutlined, CustomerServiceOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';

const { Title, Paragraph } = Typography;

interface DisplayGame {
  id: number;
  title: string;
  description: string;
  difficulty: number;
  category_id: number;
  completion_rate: number;
  accuracy: number;
  correct_moves: number;
  total_moves: number;
  piece_color: string;
}

const GamesList: React.FC = () => {
  const [games, setGames] = useState<DisplayGame[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<number | null>(null);
  const [selectedCategoryData, setSelectedCategoryData] = useState<Category | null>(null);
  const [loading, setLoading] = useState(true);
  const [isPortrait, setIsPortrait] = useState(window.innerHeight > window.innerWidth);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [pageSize, setPageSize] = useState(12);
  const [total, setTotal] = useState(0);
  const [highlightedGameId, setHighlightedGameId] = useState<number | null>(null);
  const stateRestored = useRef(false);
  const navigate = useNavigate();
  const [contactModalVisible, setContactModalVisible] = useState(false);
  const [expiredCategoryName, setExpiredCategoryName] = useState('');

  useEffect(() => {
    const handleResize = () => {
      setIsPortrait(window.innerHeight > window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    // 首先加载分类数据，但不要立即设置选中的分类
    fetchCategories();
  }, []);

  useEffect(() => {
    if (games.length > 0 && stateRestored.current) {
      const savedState = localStorage.getItem('gamesListState');
      if (savedState) {
        try {
          const state = JSON.parse(savedState);
          
          if (state.scrollPosition) {
            setTimeout(() => {
              window.scrollTo(0, state.scrollPosition);
            }, 100);
          }
          
          localStorage.removeItem('gamesListState');
        } catch (error) {
          console.error('Error restoring scroll position:', error);
        }
      }
    }
  }, [games]);

  useEffect(() => {
    if (highlightedGameId && games.length > 0) {
      setTimeout(() => {
        const element = document.getElementById(`game-item-${highlightedGameId}`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 300);
    }
  }, [highlightedGameId, games]);

  useEffect(() => {
    if (selectedCategory !== null) {
      fetchGames(selectedCategory);
    }
  }, [selectedCategory]);

  const fetchCategories = async () => {
    try {
      const data = await categoryService.getCategories();
      setCategories(data);
      
      // 在分类加载完成后，尝试从 localStorage 恢复状态
      if (!stateRestored.current) {
        const savedState = localStorage.getItem('gamesListState');
        if (savedState) {
          try {
            const state = JSON.parse(savedState);
            
            // 设置分页信息
            if (state.currentPage) {
              setCurrentPage(state.currentPage);
            }
            
            if (state.pageSize) {
              setPageSize(state.pageSize);
            }
            
            // 设置高亮的游戏ID
            if (state.selectedGameId) {
              setHighlightedGameId(state.selectedGameId);
              setTimeout(() => {
                setHighlightedGameId(null);
              }, 5000);
            }
            
            // 恢复选中的分类
            if (state.selectedCategory) {
              console.log('恢复分类:', state.selectedCategory);
              // 检查该分类是否存在于加载的分类列表中
              const categoryExists = data.some(cat => cat.id === state.selectedCategory);
              if (categoryExists) {
                setSelectedCategory(state.selectedCategory);
              } else if (data.length > 0) {
                setSelectedCategory(data[0].id); // 如果保存的分类不存在，则选择第一个
              }
            } else if (data.length > 0) {
              setSelectedCategory(data[0].id); // 如果没有保存的分类，则选择第一个
            }
            
            // 标记状态已恢复
            stateRestored.current = true;
          } catch (error) {
            console.error('Error restoring state:', error);
            localStorage.removeItem('gamesListState');
            
            // 如果恢复失败，选择第一个分类（如果有）
            if (data.length > 0) {
              setSelectedCategory(data[0].id);
            }
          }
        } else {
          // 如果没有保存的状态，选择第一个分类（如果有）
          if (data.length > 0) {
            setSelectedCategory(data[0].id);
          }
        }
      }
    } catch (error) {
      const axiosError = error as AxiosError;
      if (axiosError.response?.status === 401) {
        message.error('认证已过期，请重新登录');
        navigate('/login');
      } else {
        message.error('获取分类列表失败，请稍后重试');
      }
    }
  };

  const fetchGames = async (categoryId: number, page: number = currentPage, size: number = pageSize) => {
    try {
      setLoading(true);
      const response = await categoryService.getCategoryGames(categoryId, page, size);
      // 保存分类数据，包含有效期信息
      setSelectedCategoryData(response.category);
      
      const transformedGames: DisplayGame[] = response.games.map(game => ({
        id: game.id,
        title: game.title,
        description: game.description,
        difficulty: game.difficulty,
        category_id: game.category_id,
        completion_rate: game.user_state?.completion_rate ?? 0,
        accuracy: game.user_state?.accuracy ?? 0,
        correct_moves: game.user_state?.correct_moves ?? 0,
        total_moves: game.user_state?.total_moves ?? 0,
        piece_color: game.piece_color || 'white'
      }));
      setGames(transformedGames);
      setTotalPages(response.pages);
      setTotal(response.total);
      setCurrentPage(response.current_page);
      setPageSize(response.per_page);
    } catch (error) {
      const axiosError = error as AxiosError;
      if (axiosError.response?.status === 401) {
        message.error('认证已过期，请重新登录');
        navigate('/login');
      } else if (axiosError.response?.status === 403) {
        const responseData = axiosError.response.data as any;
        message.error(responseData?.message || '没有权限访问此课程');
        
        // 如果是因为课程过期，显示联系客服弹窗
        if (responseData?.message?.includes('已过期')) {
          const category = categories.find(cat => cat.id === selectedCategory);
          if (category) {
            setExpiredCategoryName(category.name);
            setContactModalVisible(true);
          }
        }
      } else {
        message.error('获取课程列表失败，请稍后重试');
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (page: number, size?: number) => {
    if (size && size !== pageSize) {
      setPageSize(size);
    }
    setCurrentPage(page);
    if (selectedCategory) {
      fetchGames(selectedCategory, page, size || pageSize);
    }
  };

  const handleCategoryChange = (categoryId: number) => {
    setSelectedCategory(categoryId);
    setCurrentPage(1);
  };

  const handleGameClick = (game: DisplayGame) => {
    // 检查当前选中的分类是否已过期
    if (selectedCategoryData && selectedCategoryData.valid_until) {
      const expiryDate = dayjs(selectedCategoryData.valid_until);
      const now = dayjs();
      if (expiryDate.isBefore(now)) {
        // 显示联系客服的弹窗
        setExpiredCategoryName(selectedCategoryData.name);
        setContactModalVisible(true);
        return;
      }
    }
    
    const stateToSave = {
      selectedCategory,
      currentPage,
      pageSize,
      scrollPosition: window.pageYOffset,
      selectedGameId: game.id
    };
    
    localStorage.setItem('gamesListState', JSON.stringify(stateToSave));
    
    navigate(`/game/${game.id}?color=${game.piece_color || 'white'}`);
  };

  const getCategoryIcon = (level: number) => {
    switch (level) {
      case 1:
        return <BookOutlined />;
      case 2:
        return <RiseOutlined />;
      case 3:
        return <FireOutlined />;
      case 4:
        return <TrophyOutlined />;
      default:
        return <AppstoreOutlined />;
    }
  };

  // 修改渲染有效期标签的函数，移除即将过期的提示
  const renderValidityBadge = (validUntil: string | null) => {
    if (!validUntil) {
      return null; // 无有效期限制
    }
    
    const expiryDate = dayjs(validUntil);
    const now = dayjs();
    const daysLeft = expiryDate.diff(now, 'day');
    
    if (daysLeft < 0) {
      return (
        <Tooltip title={`已过期于 ${expiryDate.format('YYYY-MM-DD')}`}>
          <Tag color="red" style={{ marginLeft: 8 }}>
            <ClockCircleOutlined /> 已过期
          </Tag>
        </Tooltip>
      );
    } else {
      // 有效期内的课程不显示任何标签
      return null;
    }
  };

  // 检查分类是否过期
  const isCategoryExpired = (category: Category): boolean => {
    if (!category.valid_until) return false;
    const expiryDate = dayjs(category.valid_until);
    const now = dayjs();
    return expiryDate.isBefore(now);
  };

  return (
    <MainLayout>
      {/* 联系客服弹窗 */}
      <Modal
        title="课程已过期"
        open={contactModalVisible}
        onCancel={() => setContactModalVisible(false)}
        footer={[
          <Button key="back" onClick={() => setContactModalVisible(false)}>
            关闭
          </Button>,
          <Button 
            key="contact" 
            type="primary" 
            icon={<CustomerServiceOutlined />}
            onClick={() => {
              // 这里可以添加打开客服聊天或拨打电话等功能
              message.info('请联系客服续订课程');
              setContactModalVisible(false);
            }}
          >
            联系客服
          </Button>
        ]}
      >
        <p>您的课程 "{expiredCategoryName}" 已过期，请联系客服续订。</p>
        <p>微信: chaoyuejm</p>
      </Modal>

      {/* Banner 区域 */}
      <div style={{ 
        background: 'linear-gradient(135deg, #1890ff 0%, #52c41a 100%)',
        padding: '24px',
        color: '#fff',
        textAlign: 'center',
        width: '100%',
        boxSizing: 'border-box',
        marginBottom: '24px',
        borderRadius: '8px'
      }}>
        <Title level={2} style={{ color: '#fff', margin: 0 }}>
          超玥国际象棋训练
        </Title>
        <Paragraph style={{ 
          color: 'rgba(255, 255, 255, 0.85)', 
          margin: '8px 0 0 0',
          fontSize: '16px'
        }}>
          通过实战训练提升你的国际象棋水平
        </Paragraph>
      </div>

      <Row gutter={24} wrap={isPortrait}>
        {/* 分类选择区域 */}
        <Col flex={isPortrait ? '100%' : '300px'} style={{ marginBottom: isPortrait ? '16px' : 0 }}>
          <Card
            bordered={false}
            bodyStyle={{ padding: '16px' }}
          >
            <div style={{ 
              display: 'flex',
              flexDirection: 'column',
              gap: '12px'
            }}>
              <Title level={4} style={{ 
                margin: 0,
                fontSize: '18px',
                color: '#1f1f1f'
              }}>
                我的课程
              </Title>
              
              <div style={{
                height: '1px',
                background: 'linear-gradient(to right, transparent, #e8e8e8 20%, #e8e8e8 80%, transparent)',
                margin: '12px 0'
              }} />
              
              {categories.length > 0 ? (
                <div style={{
                  display: 'flex',
                  flexDirection: isPortrait ? 'row' : 'column',
                  flexWrap: isPortrait ? 'wrap' : 'nowrap',
                  gap: '8px'
                }}>
                  {categories.map(category => {
                    const expired = isCategoryExpired(category);
                    return (
                    <Button
                      key={category.id}
                      type={selectedCategory === category.id ? 'primary' : 'default'}
                      onClick={() => handleCategoryChange(category.id)}
                      icon={getCategoryIcon(category.difficulty_level)}
                      style={{
                        borderRadius: '20px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        padding: '4px 16px',
                        height: 'auto',
                        minHeight: '36px',
                        width: isPortrait ? 'auto' : '100%',
                        justifyContent: isPortrait ? 'center' : 'flex-start',
                        position: 'relative',
                        overflow: 'visible',
                        ...(selectedCategory === category.id ? {
                          background: 'linear-gradient(120deg, #1890ff 0%, #52c41a 100%)',
                          border: 'none',
                          color: '#fff'
                        } : {
                          background: '#fff',
                          border: '1px solid #e8e8e8'
                        })
                      }}
                    >
                      {/* 过期悬浮标签 */}
                      {expired && (
                        <div style={{
                          position: 'absolute',
                          right: '-8px',
                          top: '-5px',
                          background: 'rgba(255, 77, 79, 0.8)',
                          color: 'white',
                          fontSize: '12px',
                          padding: '2px 8px',
                          borderRadius: '10px',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                          zIndex: 5,
                          fontWeight: 'bold',
                          letterSpacing: '0.5px',
                          transform: 'scale(0.9)'
                        }}>
                          已过期
                        </div>
                      )}
                      
                      <div style={{ 
                        display: 'flex', 
                        flexDirection: 'column', 
                        alignItems: isPortrait ? 'center' : 'flex-start',
                        width: '100%'
                      }}>
                        <div style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: '4px',
                          justifyContent: isPortrait ? 'center' : 'flex-start',
                          width: '100%'
                        }}>
                          <span>{category.name}</span>
                          <span style={{ 
                            fontSize: '12px',
                            opacity: 0.8,
                            marginLeft: '4px'
                          }}>
                            ({category.games_count})
                          </span>
                        </div>
                      </div>
                    </Button>
                  )})}
                </div>
              ) : (
                <div style={{
                  textAlign: 'center',
                  padding: '24px',
                  color: '#999',
                  background: '#fafafa',
                  borderRadius: '8px'
                }}>
                  <AppstoreOutlined style={{ fontSize: '24px', marginBottom: '8px' }} />
                  <div>暂无课程分类</div>
                </div>
              )}
            </div>
          </Card>
        </Col>

        {/* 游戏列表 */}
        <Col flex={isPortrait ? '100%' : 'auto'}>
          <Card
            bordered={false}
            bodyStyle={{ 
              padding: '16px',
              background: '#fff',
              borderRadius: '8px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
            }}
          >
            <List
              grid={{
                gutter: 16,
                xs: 1,
                sm: isPortrait ? 2 : 2,
                md: isPortrait ? 2 : 3,
                lg: isPortrait ? 3 : 4,
                xl: isPortrait ? 4 : 4,
                xxl: isPortrait ? 4 : 4,
              }}
              dataSource={games}
              loading={{
                spinning: loading && categories.length > 0,
                tip: "数据加载中..."
              }}
              locale={{ 
                emptyText: '暂无课程内容'
              }}
              pagination={{
                current: currentPage,
                pageSize: pageSize,
                total: total,
                onChange: handlePageChange,
                showSizeChanger: true,
                showQuickJumper: true,
                pageSizeOptions: ['12', '24', '36', '48'],
                showTotal: (total) => `共 ${total} 个课程`,
                position: 'bottom',
                align: 'center',
                style: { marginTop: '24px' }
              }}
              renderItem={game => {
                // 检查分类是否已过期
                const categoryExpired = selectedCategoryData && 
                  selectedCategoryData.valid_until && 
                  dayjs(selectedCategoryData.valid_until).isBefore(dayjs());
                
                return (
                <List.Item id={`game-item-${game.id}`}>
                  <Card
                    hoverable
                    onClick={() => handleGameClick(game)}
                    style={{ 
                      background: '#fff',
                      transition: 'all 0.3s ease',
                      borderRadius: '8px',
                      padding: '16px',
                      height: '100%',
                      position: 'relative',
                      overflow: 'hidden',
                      border: categoryExpired 
                        ? '1px solid #e0e0e0' 
                        : (game.id === highlightedGameId ? '1px solid #91d5ff' : 'none'),
                      boxShadow: categoryExpired
                        ? 'none'
                        : (game.id === highlightedGameId 
                            ? '0 4px 12px rgba(24, 144, 255, 0.15)' 
                            : '0 2px 8px rgba(0,0,0,0.05)'),
                      filter: categoryExpired ? 'grayscale(0.9)' : 'none',
                      transform: categoryExpired ? 'scale(0.98)' : 'scale(1)'
                    }}
                    bodyStyle={{
                      padding: 0,
                      height: '100%',
                      position: 'relative'
                    }}
                  >
                    {/* 内容容器 */}
                    <div style={{ 
                      position: 'relative', 
                      height: '100%', 
                      opacity: categoryExpired ? 0.75 : 1
                    }}>
                      {/* 右上角小标签 */}
                      {categoryExpired && (
                        <div style={{
                          position: 'absolute',
                          top: 0,
                          right: 0,
                          width: '80px',
                          height: '80px',
                          overflow: 'hidden',
                          zIndex: 2,
                          pointerEvents: 'none'
                        }}>
                          <div style={{
                            position: 'absolute',
                            top: '12px',
                            right: '-22px',
                            transform: 'rotate(45deg)',
                            background: 'rgba(220, 220, 220, 0.8)',
                            boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
                            padding: '2px 24px',
                            fontSize: '10px',
                            color: '#888',
                            fontWeight: 500,
                            letterSpacing: '0.5px',
                            textTransform: 'uppercase'
                          }}>
                            已到期
                          </div>
                        </div>
                      )}

                      {/* 虚化蒙版 - 只对已完成的非过期项目显示 */}
                      {game.completion_rate === 100 && !categoryExpired && (
                        <div style={{
                          position: 'absolute',
                          top: '-16px',
                          left: '-16px',
                          right: '-16px',
                          bottom: '-16px',
                          background: 'rgba(255, 255, 255, 0.08)',
                          backdropFilter: 'blur(1px)',
                          pointerEvents: 'none'
                        }} />
                      )}

                      {/* 标题区域 */}
                      <div style={{ 
                        marginBottom: '16px',
                        position: 'relative',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: '8px'
                      }}>
                        <Title level={5} style={{ 
                          margin: 0,
                          fontSize: '14px',
                          fontWeight: 600,
                          color: categoryExpired ? '#777' : '#262626',
                          flex: 1,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}>{game.title}</Title>

                        {/* 完成标签 */}
                        {game.completion_rate === 100 && (
                          <div style={{
                            background: categoryExpired 
                              ? '#e0e0e0'
                              : 'linear-gradient(135deg, #52c41a 0%, #1890ff 100%)',
                            padding: '2px 8px',
                            borderRadius: '4px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '3px',
                            flexShrink: 0
                          }}>
                            <CheckCircleOutlined style={{ 
                              fontSize: '12px', 
                              color: categoryExpired ? '#999' : '#ffffff'
                            }} />
                            <span style={{ 
                              color: categoryExpired ? '#999' : '#ffffff',
                              fontSize: '12px',
                              lineHeight: 1
                            }}>已完成</span>
                          </div>
                        )}
                      </div>

                      {/* 完成进度区域 */}
                      <div style={{ marginBottom: '16px' }}>
                        <div style={{ 
                          display: 'flex', 
                          justifyContent: 'space-between', 
                          marginBottom: '4px',
                          fontSize: '14px',
                          color: categoryExpired ? '#888' : '#666'
                        }}>
                          <span>完成进度</span>
                          <span style={{ 
                            color: categoryExpired ? '#888' : '#52c41a',
                            fontWeight: 500
                          }}>{game.completion_rate}%</span>
                        </div>
                        <Progress 
                          percent={game.completion_rate}
                          size="small"
                          showInfo={false}
                          strokeColor={categoryExpired 
                            ? { '0%': '#c0c0c0', '100%': '#a0a0a0' }
                            : { '0%': '#108ee9', '100%': '#87d068' }
                          }
                          style={{ margin: 0 }}
                        />
                      </div>

                      {/* 正确率区域 */}
                      <div style={{ marginBottom: '16px' }}>
                        <div style={{ 
                          display: 'flex', 
                          justifyContent: 'space-between', 
                          marginBottom: '4px',
                          fontSize: '14px',
                          color: categoryExpired ? '#888' : '#666'
                        }}>
                          <span>正确率</span>
                          <span style={{ 
                            color: categoryExpired ? '#888' : (game.accuracy >= 80 ? '#52c41a' : '#fa8c16'),
                            fontWeight: 500
                          }}>{game.accuracy}%</span>
                        </div>
                        <Progress 
                          percent={game.accuracy}
                          size="small"
                          showInfo={false}
                          strokeColor={categoryExpired
                            ? { '0%': '#c0c0c0', '100%': '#a0a0a0' }
                            : { '0%': '#ffa39e', '100%': '#52c41a' }
                          }
                          style={{ margin: 0 }}
                        />
                      </div>

                      {/* 步数统计区域 */}
                      <div style={{ 
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr',
                        gap: '8px'
                      }}>
                        <div style={{ 
                          background: categoryExpired ? '#f7f7f7' : '#fafafa',
                          padding: '8px',
                          borderRadius: '6px',
                          textAlign: 'center'
                        }}>
                          <div style={{ 
                            fontSize: '12px',
                            color: categoryExpired ? '#888' : '#666',
                            marginBottom: '2px'
                          }}>正确步数</div>
                          <div style={{ 
                            fontSize: '18px',
                            color: categoryExpired ? '#888' : '#52c41a',
                            fontWeight: 600
                          }}>{game.correct_moves}</div>
                        </div>
                        <div style={{ 
                          background: categoryExpired ? '#f7f7f7' : '#fafafa',
                          padding: '8px',
                          borderRadius: '6px',
                          textAlign: 'center'
                        }}>
                          <div style={{ 
                            fontSize: '12px',
                            color: categoryExpired ? '#888' : '#666',
                            marginBottom: '2px'
                          }}>总步数</div>
                          <div style={{ 
                            fontSize: '18px',
                            color: categoryExpired ? '#888' : '#1f1f1f',
                            fontWeight: 600
                          }}>{game.total_moves}</div>
                        </div>
                      </div>
                    </div>
                  </Card>
                </List.Item>
              )}}
            />
          </Card>
        </Col>
      </Row>
    </MainLayout>
  );
};

export default GamesList; 